import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export default function ContactPage() {
  const { t, trSetting } = useI18n();
  const [s, setS] = useState({});
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e2) {
      toast.error("Failed to send");
    } finally { setSubmitting(false); }
  }

  return (
    <SiteLayout>
      <Seo title={`${t("contact.title")} · Berdhill Rooms`} description="Contact Berdhill Rooms in Sarandë, Albania." />
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("contact.subtitle")}</div>
        <h1 className="font-display text-5xl md:text-6xl mt-3">{t("contact.title")}</h1>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card icon={<MapPin />} label="Address" value={trSetting(s, "address")} />
          <Card icon={<Phone />} label="Phone" value={trSetting(s, "phone")} />
          <Card icon={<Mail />} label="Email" value={trSetting(s, "email")} />
        </div>

        <div className="mt-16 grid lg:grid-cols-5 gap-10">
          <form onSubmit={submit} className="lg:col-span-3 bg-white border border-brand-border rounded-sm p-6 md:p-8 space-y-4">
            <h2 className="font-display text-2xl">{t("contact.send")}</h2>
            <Field label={t("contact.name")}>
              <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="berdhill-contact-input" />
            </Field>
            <Field label={t("book.email")}>
              <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="berdhill-contact-input" />
            </Field>
            <Field label={t("contact.subj")}>
              <input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="berdhill-contact-input" />
            </Field>
            <Field label={t("contact.message")}>
              <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="berdhill-contact-input" />
            </Field>
            <button data-testid="contact-submit" disabled={submitting} type="submit" className="bg-brand-primary hover:bg-brand-primaryHover text-white px-6 py-3 rounded-sm text-sm flex items-center gap-2 disabled:opacity-50">
              <Send size={14} /> {submitting ? "…" : t("contact.send")}
            </button>
            <style>{`.berdhill-contact-input{width:100%;border:1px solid #E6E2DB;border-radius:.125rem;padding:.625rem .75rem;font-size:.875rem;background:#fff}`}</style>
          </form>
          <div className="lg:col-span-2 aspect-[3/4] lg:aspect-auto overflow-hidden rounded-sm">
            <iframe
              title="Berdhill Rooms location"
              src={trSetting(s, "maps_embed_url") || "https://www.openstreetmap.org/export/embed.html?bbox=20.0%2C39.86%2C20.04%2C39.89&layer=mapnik&marker=39.875%2C20.02"}
              className="w-full h-full border-0 min-h-[400px]"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}

function Card({ icon, label, value }) {
  return (
    <div className="bg-white border border-brand-border rounded-sm p-6">
      <div className="text-brand-primary mb-3">{icon}</div>
      <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{label}</div>
      <div className="mt-2 text-sm">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-brand-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
